import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { useCreateSponsor } from 'queries/sponsors.query'
import Dropzone from '../../settings/components/Dropzone'

// Validation schema for the form fields
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Sponsor name is required.'),
  website: Yup.string()
    .url('Website must be a valid URL.')
    .required('Website is required.'),
})

export function CreateSponsorModal ({ isOpen, onClose }) {
  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')

  const toast = useToast()
  const { mutate, isPending } = useCreateSponsor()

  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [logoError, setLogoError] = useState(null)

  useEffect(() => {
    if (!isOpen) {
      setLogoFile(null)
      setLogoError(null)
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview)
        setLogoPreview(null)
      }
    }
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview)
      }
    }
  }, [isOpen])

  const handleFileChange = file => {
    setLogoFile(file)
    setLogoError(null)
    if (file) {
      setLogoPreview(URL.createObjectURL(file))
    } else {
      setLogoPreview(null)
    }
  }

  const initialValues = {
    name: '',
    website: '',
  }

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    // Check if a logo file is present
    if (!logoFile) {
      setLogoError('A logo file is required.')
      setSubmitting(false)
      return
    }

    const formData = new FormData()
    formData.append('name', values.name)
    formData.append('website', values.website)
    formData.append('logo', logoFile)

    mutate(formData, {
      onSuccess: () => {
        toast({
          title: 'Sponsor created.',
          description: 'The new sponsor has been added successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
        resetForm()
        setLogoFile(null)
        onClose()
      },
      onError: error => {
        const backendMessage = error.response?.data?.message || error.response?.data?.error || error?.message || 'An error occurred while creating the sponsor.'
        toast({
          title: 'Creation failed.',
          description: backendMessage,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      },
      onSettled: () => {
        setSubmitting(false)
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Sponsor</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align='center'>
                  <FormControl
                    id='name'
                    isInvalid={errors.name && touched.name}
                    isRequired
                  >
                    <FormLabel>Sponsor Name</FormLabel>
                    <Field
                      as={Input}
                      name='name'
                      color={inputColor}
                      backgroundColor={backgroundColor}
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                  </FormControl>
                  <FormControl
                    id='website'
                    isInvalid={errors.website && touched.website}
                    isRequired
                  >
                    <FormLabel>Website URL</FormLabel>
                    <Field
                      as={Input}
                      name='website'
                      color={inputColor}
                      backgroundColor={backgroundColor}
                    />
                    <FormErrorMessage>{errors.website}</FormErrorMessage>
                  </FormControl>
                  <FormControl id='logo' isInvalid={!!logoError} isRequired>
                    <FormLabel>Logo</FormLabel>
                    <Dropzone
                      title='Upload Logo'
                      onFileChange={handleFileChange}
                      preview={logoPreview}
                    />
                    <FormErrorMessage>{logoError}</FormErrorMessage>
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme='blue' type='submit' isLoading={isPending}>
                  Create
                </Button>
                <Button colorScheme='gray' ml={3} onClick={onClose}>
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}
